/**
 * Contaminant Stats Column — the 5 cards (Contaminant, Initial PSH, PSH Depleted,
 * Peak Dissolved, Remediation) stacked vertically. Lives at the top of the
 * polygon site page alongside the Cost column and Reg Library column.
 *
 * Data preference order, per field:
 *   1. `observedMetrics` — values derived from real DB measurements
 *      (analytical_results, well_gaugings, recovery_events). Each field
 *      carries an `as_of_date` and `source_note` so we can show provenance.
 *   2. `threePlumeResult` — the physics-model simulation, used as a fallback
 *      when there's not enough measured data to compute the observed value.
 */
import type { ThreePlumeResponse, ObservedMetricsResponse, ObservedField } from '../../api/polygon';

interface ContaminantStatsColumnProps {
  threePlumeResult?: ThreePlumeResponse | null;
  observedMetrics?: ObservedMetricsResponse | null;
}

function fieldHas<T>(f: ObservedField<T> | undefined | null): boolean {
  return !!f && f.value !== null && f.value !== undefined;
}

function tooltipFor<T>(label: string, f: ObservedField<T> | undefined | null): string {
  if (!fieldHas(f)) return label;
  const parts: string[] = [];
  if (f!.source_note) parts.push(f!.source_note);
  if (f!.as_of_date) parts.push(`as of ${f!.as_of_date}`);
  return parts.length ? `${label} — ${parts.join(' · ')}` : label;
}

export function ContaminantStatsColumn({ threePlumeResult, observedMetrics }: ContaminantStatsColumnProps) {
  if (!threePlumeResult && !observedMetrics) return null;

  // Lead contaminant — prefer observed, fall back to three-plume's contaminant_type.
  const contaminantValue =
    fieldHas(observedMetrics?.lead_contaminant)
      ? observedMetrics!.lead_contaminant.value
      : threePlumeResult?.contaminant_type || 'Unknown';
  const contaminantTip = tooltipFor('Contaminant with highest exceedance ratio', observedMetrics?.lead_contaminant);

  // Initial PSH — observed preferred.
  const initialPshObs = observedMetrics?.initial_psh_lbs;
  const initialPshValue = fieldHas(initialPshObs)
    ? `${initialPshObs!.value!.toLocaleString(undefined, { maximumFractionDigits: 1 })} lbs`
    : threePlumeResult?.initial_psh_mass_lbs != null
    ? `${threePlumeResult.initial_psh_mass_lbs.toLocaleString()} lbs`
    : null;
  const initialPshTip = tooltipFor('Initial PSH mass', initialPshObs);

  // PSH Depleted — observed preferred.
  const depletedObs = observedMetrics?.psh_depleted_pct;
  const depletedValue = fieldHas(depletedObs)
    ? `${depletedObs!.value!.toFixed(1)}%`
    : threePlumeResult?.psh_depletion_percent != null
    ? `${threePlumeResult.psh_depletion_percent.toFixed(1)}%`
    : null;
  const depletedTip = tooltipFor('PSH depleted from peak', depletedObs);

  // Peak dissolved area.
  const peakObs = observedMetrics?.peak_dissolved_sqft;
  const peakValue = fieldHas(peakObs)
    ? `${peakObs!.value!.toLocaleString()} sqft`
    : threePlumeResult?.peak_dissolved_area_sqft != null
    ? `${threePlumeResult.peak_dissolved_area_sqft.toLocaleString()} sqft`
    : null;
  const peakTip = tooltipFor('Peak dissolved-plume footprint', peakObs);

  // Remediation type.
  const remObs = observedMetrics?.remediation;
  const remValue =
    fieldHas(remObs)
      ? remObs!.value
      : threePlumeResult?.remediation_type || 'None';
  const remTip = tooltipFor('Remediation activity', remObs);

  // Observed-vs-modeled tag for the corner of each card.
  const sourceTag = (used: boolean) => (
    <span className={`ml-1 text-[8px] uppercase tracking-wider ${used ? 'text-cyan-500/70' : 'text-mono-600'}`}>
      {used ? 'measured' : 'modeled'}
    </span>
  );

  return (
    <div className="space-y-2">
      {threePlumeResult?.psh_data_available === false && (
        <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-2">
          <div className="text-amber-400 text-[11px] font-medium">⚠️ No PSH/LNAPL data</div>
          <div className="text-amber-600 text-[9px] mt-0.5">
            Upload gauging with product thickness to enable three-plume modeling.
          </div>
        </div>
      )}

      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3" title={contaminantTip}>
        <div className="text-[10px] text-mono-500 uppercase flex items-center justify-between">
          Contaminant {sourceTag(fieldHas(observedMetrics?.lead_contaminant))}
        </div>
        <div className="text-sm font-semibold text-mono-100">
          {contaminantValue || <span className="text-mono-600">Unknown</span>}
        </div>
      </div>

      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3" title={initialPshTip}>
        <div className="text-[10px] text-mono-500 uppercase flex items-center justify-between">
          Initial PSH {sourceTag(fieldHas(initialPshObs))}
        </div>
        <div className="text-sm font-semibold text-amber-400">
          {initialPshValue || <span className="text-mono-600">No Data</span>}
        </div>
      </div>

      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3" title={depletedTip}>
        <div className="text-[10px] text-mono-500 uppercase flex items-center justify-between">
          PSH Depleted {sourceTag(fieldHas(depletedObs))}
        </div>
        <div className="text-sm font-semibold text-mono-100">
          {depletedValue || <span className="text-mono-600">No Data</span>}
        </div>
      </div>

      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3" title={peakTip}>
        <div className="text-[10px] text-mono-500 uppercase flex items-center justify-between">
          Peak Dissolved {sourceTag(fieldHas(peakObs))}
        </div>
        <div className="text-sm font-semibold text-cyan-400">
          {peakValue || <span className="text-mono-600">No Data</span>}
        </div>
      </div>

      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3" title={remTip}>
        <div className="text-[10px] text-mono-500 uppercase flex items-center justify-between">
          Remediation {sourceTag(fieldHas(remObs))}
        </div>
        <div className="text-sm font-semibold text-mono-100">
          {remValue || 'None'}
        </div>
      </div>
    </div>
  );
}
